import { useEffect, useState } from 'react'
import { cx } from './cx.ts'
import { motionReduced } from './motion.tsx'

export interface BarDatum {
  label: string
  value: number
  /** Longer text for the hover title (defaults to the label). */
  title?: string
}

/**
 * Plain bar chart: one bar per datum, scaled to the largest value. Negative
 * values draw as a small red stub. Labels thin out so they never overlap; every
 * bar still has a hover title, and screen readers get `summary`.
 *
 * Bars are drawn full height and scaled (`scaleY`), so a changed value eases to its new
 * height. `grow` makes them rise from nothing once, when the chart first shows.
 */
export function BarChart({
  data,
  format,
  summary,
  height = 160,
  highlight,
  grow = false,
  className,
}: {
  data: readonly BarDatum[]
  format: (value: number) => string
  summary: string
  height?: number
  /** Index of the bar to pick out (today, say); the others go quieter. */
  highlight?: number
  /** Rise from nothing on the first render (the dashboard, once a day). */
  grow?: boolean
  className?: string
}) {
  const [grown, setGrown] = useState(() => !grow || motionReduced())
  useEffect(() => {
    if (grown) return
    // One frame at zero height, then the real heights: the transition does the rest.
    const frame = requestAnimationFrame(() => setGrown(true))
    return () => cancelAnimationFrame(frame)
  }, [grown])
  const max = Math.max(1, ...data.map((d) => d.value))
  // A bar of nothing (or below nothing) is a 3px stub.
  const stub = 3 / height
  const step = Math.max(1, Math.ceil(data.length / 8))
  return (
    <div role="img" aria-label={summary} className={cx('w-full', className)}>
      <div className="flex items-end gap-px sm:gap-1" style={{ height }}>
        {data.map((d, i) => (
          <div
            key={i}
            title={`${d.title ?? d.label}: ${format(d.value)}`}
            className="group flex h-full min-w-0 flex-1 items-end"
          >
            <div
              className={cx(
                'h-full w-full origin-bottom rounded-t-sm transition-[transform,background-color] duration-300 ease-out',
                d.value < 0
                  ? 'bg-danger'
                  : highlight === undefined
                    ? 'bg-primary/80 group-hover:bg-primary'
                    : i === highlight
                      ? 'bg-primary'
                      : 'bg-primary/40 group-hover:bg-primary/70',
              )}
              style={{
                transform: `scaleY(${
                  !grown ? 0 : d.value > 0 ? Math.max(0.015, d.value / max, stub) : stub
                })`,
                opacity: d.value === 0 ? 0.25 : 1,
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex gap-px border-t border-border pt-1 sm:gap-1" aria-hidden>
        {data.map((d, i) => (
          <div key={i} className="min-w-0 flex-1 overflow-visible text-[11px] text-subtle">
            {i % step === 0 && <span className="block whitespace-nowrap">{d.label}</span>}
          </div>
        ))}
      </div>
    </div>
  )
}
